import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { X, Loader2, User, Lock, UserPlus, Mail, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api } from '../../services/api'
import { toast } from 'sonner'
import PasswordResetModal from './PasswordResetModal'

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

type AuthMode = 'login' | 'register'
type RegistrationStep = 'credentials' | 'verify-email'

export default function AuthModal({ isOpen, onClose, onSuccess }: AuthModalProps) {
  const { t } = useTranslation(['auth', 'common'])
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const [mode, setMode] = useState<AuthMode>('login')
  const [registrationStep, setRegistrationStep] = useState<RegistrationStep>('credentials')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [email, setEmail] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [error, setError] = useState('')
  const [referralCode, setReferralCode] = useState<string | null>(null)
  const [resendCooldown, setResendCooldown] = useState(0)
  const [verifyEmailFromLogin, setVerifyEmailFromLogin] = useState(false)
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false)

  // Check for referral code in URL and validate it
  useEffect(() => {
    const refCode = searchParams.get('ref')
    if (refCode) {
      // Validate referral code with backend
      api.validateReferralCode(refCode)
        .then((response) => {
          if (response.success && response.data.valid) {
            setReferralCode(refCode)
            setError('') // Clear any previous errors
            // Auto-switch to register mode when coming from valid referral link
            setMode('register')
          } else {
            setError(response.data.message || t('auth:errors.invalidReferral'))
            setReferralCode(null)
          }
        })
        .catch(() => {
          setError(t('auth:errors.validateReferralFailed'))
          setReferralCode(null)
        })
    }
  }, [searchParams])

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  const loginMutation = useMutation({
    mutationFn: async () => {
      setError('')
      return await api.loginWithPassword(username, password)
    },
    onSuccess: (data) => {
      // Check if email verification is required
      if ((data as any).requiresEmailVerification) {
        setEmail((data as any).email)
        setMode('register')
        setRegistrationStep('verify-email')
        toast.info(t('auth:success.pleaseVerify'))
        return
      }

      toast.success(t('auth:success.loginSuccessful'))
      setUsername('')
      setPassword('')
      setError('')
      onSuccess?.()
      onClose()
      // Invalidate user query to refresh auth state without page reload
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
    },
    onError: (error: any) => {
      const status = error.response?.status
      const data = error.response?.data

      // User exists but email not verified: show verify-email step so they can verify later
      if (status === 403 && data?.requiresEmailVerification && data?.email) {
        setEmail(data.email)
        setMode('register')
        setRegistrationStep('verify-email')
        setVerifyEmailFromLogin(true)
        setError('')
        toast.info(t('auth:success.pleaseVerify'))
        return
      }

      const errorMessage = data?.message || t('auth:errors.loginFailed')
      setError(errorMessage)
      console.error('Login error:', error)
    },
  })

  const registerMutation = useMutation({
    mutationFn: async () => {
      setError('')
      return await api.register(username, password, email, referralCode || undefined)
    },
    onSuccess: () => {
      toast.success(t('auth:success.registrationSuccessful'))
      setRegistrationStep('verify-email')
      setError('')
      setResendCooldown(60) // 60 second cooldown
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('auth:errors.registrationFailed')
      setError(errorMessage)
      console.error('Registration error:', error)
    },
  })

  const verifyEmailMutation = useMutation({
    mutationFn: async () => {
      setError('')
      return await api.verifyEmail(email, otpCode)
    },
    onSuccess: () => {
      toast.success(t('auth:success.emailVerified'))
      setUsername('')
      setPassword('')
      setEmail('')
      setOtpCode('')
      setError('')
      setRegistrationStep('credentials')
      onSuccess?.()
      onClose()
      // Invalidate user query to refresh auth state
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || t('auth:errors.verificationFailed')
      setError(errorMessage)
      console.error('Verification error:', error)
    },
  })

  const resendOTPMutation = useMutation({
    mutationFn: async () => {
      setError('')
      return await api.resendOTP(email, 'registration')
    },
    onSuccess: () => {
      toast.success(t('auth:success.codeSent'))
      setResendCooldown(60) // 60 second cooldown
      setError('')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || t('auth:errors.resendFailed')
      setError(errorMessage)
      console.error('Resend OTP error:', error)
    },
  })

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!username || !password) {
      toast.error(t('auth:errors.usernamePasswordRequired'))
      return
    }

    if (mode === 'register' && !email) {
      toast.error(t('auth:errors.emailRequired'))
      return
    }

    try {
      if (mode === 'login') {
        await loginMutation.mutateAsync()
      } else {
        await registerMutation.mutateAsync()
      }
    } catch {
      // Error is already handled by onError callback
    }
  }

  const handleVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!otpCode || otpCode.length !== 6) {
      toast.error(t('auth:errors.verificationCodeRequired'))
      return
    }

    try {
      await verifyEmailMutation.mutateAsync()
    } catch {
      // Error is already handled by onError callback
    }
  }

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return

    try {
      await resendOTPMutation.mutateAsync()
    } catch {
      // Error is already handled by onError callback
    }
  }

  const handleClose = () => {
    if (!loginMutation.isPending && !registerMutation.isPending && !verifyEmailMutation.isPending) {
      setUsername('')
      setPassword('')
      setEmail('')
      setOtpCode('')
      setError('')
      setMode('login')
      setRegistrationStep('credentials')
      setVerifyEmailFromLogin(false)
      onClose()
    }
  }

  const handleModeToggle = () => {
    setMode(mode === 'login' ? 'register' : 'login')
    setRegistrationStep('credentials')
    setError('')
    setOtpCode('')
  }

  const handleBackToCredentials = () => {
    setRegistrationStep('credentials')
    setOtpCode('')
    setError('')
    if (verifyEmailFromLogin) {
      setMode('login')
      setVerifyEmailFromLogin(false)
    }
  }

  if (!isOpen) return null

  const isPending = loginMutation.isPending || registerMutation.isPending || verifyEmailMutation.isPending || resendOTPMutation.isPending

  const modalContent = (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-background-card rounded-xl max-w-md w-full border border-green-900/20 my-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-green-900/20">
          <h2 className="text-2xl font-bold text-white">
            {registrationStep === 'verify-email' ? t('auth:verifyEmail') : mode === 'login' ? t('auth:login') : t('auth:createAccount')}
          </h2>
          {!isPending && (
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {registrationStep === 'credentials' ? (
            <>
              {/* Referral Banner */}
              {referralCode && mode === 'register' && (
                <div className="mb-4 p-3 bg-gold-500/10 border border-gold-500/30 rounded-lg flex items-center gap-3">
                  <UserPlus className="h-5 w-5 text-gold-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gold-300 font-medium">{t('auth:referral.invited')}</p>
                    <p className="text-xs text-gray-400">
                      {t('auth:referral.code')} <span className="font-mono text-gold-400">{referralCode}</span>
                    </p>
                  </div>
                </div>
              )}

              <form onSubmit={handleCredentialsSubmit} className="space-y-4">
                {/* Username */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('auth:username')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => {
                        setUsername(e.target.value)
                        setError('')
                      }}
                      className="w-full pl-10 pr-4 py-3 bg-background-elevated border border-green-900/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gold-500/50"
                      placeholder={t('auth:placeholders.username')}
                      disabled={isPending}
                      required
                    />
                  </div>
                  {mode === 'register' && (
                    <p className="mt-1 text-xs text-gray-500">
                      {t('auth:hints.usernameFormat')}
                    </p>
                  )}
                </div>

                {/* Email (Required for registration) */}
                {mode === 'register' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('auth:email')} <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value)
                          setError('')
                        }}
                        className="w-full pl-10 pr-4 py-3 bg-background-elevated border border-green-900/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gold-500/50"
                        placeholder={t('auth:placeholders.email')}
                        disabled={isPending}
                        required
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {t('auth:hints.emailVerification')}
                    </p>
                  </div>
                )}

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('auth:password')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value)
                        setError('')
                      }}
                      className="w-full pl-10 pr-4 py-3 bg-background-elevated border border-green-900/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gold-500/50"
                      placeholder={t('auth:placeholders.password')}
                      disabled={isPending}
                      required
                    />
                  </div>
                  {mode === 'register' && (
                    <p className="mt-1 text-xs text-gray-500">
                      {t('auth:hints.passwordMinLength')}
                    </p>
                  )}
                </div>

                {/* Error Display */}
                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-sm text-red-400 text-center">{error}</p>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isPending}
                  className="w-full btn-gold py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
                >
                  {isPending ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      {mode === 'login' ? t('auth:buttons.loggingIn') : t('auth:buttons.creatingAccount')}
                    </>
                  ) : (
                    <>{mode === 'login' ? t('auth:buttons.login') : t('auth:buttons.createAccount')}</>
                  )}
                </button>
              </form>

              {mode === 'login' && (
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={() => setShowPasswordResetModal(true)}
                    disabled={isPending}
                    className="text-sm text-gray-400 hover:text-gold-500 transition-colors disabled:opacity-50"
                  >
                    {t('auth:forgotPassword')}
                  </button>
                </div>
              )}

              {/* Toggle Mode */}
              <div className="mt-6 text-center">
                <button
                  onClick={handleModeToggle}
                  disabled={isPending}
                  className="text-sm text-gray-400 hover:text-gold-500 transition-colors disabled:opacity-50"
                >
                  {mode === 'login' ? (
                    <>
                      {t('auth:toggleMode.noAccount')} <span className="text-gold-500 font-semibold">{t('auth:toggleMode.signUp')}</span>
                    </>
                  ) : (
                    <>
                      {t('auth:toggleMode.haveAccount')} <span className="text-gold-500 font-semibold">{t('auth:toggleMode.loginLink')}</span>
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Email Verification Step */}
              <div className="mb-6 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gold-500/10 rounded-full mb-4">
                  <Mail className="h-8 w-8 text-gold-400" />
                </div>
                <p className="text-gray-300 mb-2">
                  {t('auth:verification.sentTo')}
                </p>
                <p className="text-white font-semibold mb-4">{email}</p>
                <p className="text-sm text-gray-400">
                  {t('auth:verification.enterCode')}
                </p>
              </div>

              <form onSubmit={handleVerifySubmit} className="space-y-4">
                {/* OTP Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2 text-center">
                    {t('auth:verificationCode')}
                  </label>
                  <input
                    type="text"
                    value={otpCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                      setOtpCode(value)
                      setError('')
                    }}
                    className="w-full px-4 py-4 text-center text-2xl tracking-[0.5em] bg-background-elevated border border-green-900/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gold-500/50 font-mono"
                    placeholder={t('auth:placeholders.verificationCode')}
                    disabled={isPending}
                    maxLength={6}
                    required
                  />
                  <p className="mt-2 text-xs text-gray-500 text-center">
                    {t('auth:hints.codeExpires')}
                  </p>
                </div>

                {/* Error Display */}
                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-sm text-red-400 text-center">{error}</p>
                  </div>
                )}

                {/* Verify Button */}
                <button
                  type="submit"
                  disabled={isPending || otpCode.length !== 6}
                  className="w-full btn-gold py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {verifyEmailMutation.isPending ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      {t('auth:buttons.verifying')}
                    </>
                  ) : (
                    <>{t('auth:buttons.verifyEmail')}</>
                  )}
                </button>

                {/* Resend Code */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={resendCooldown > 0 || resendOTPMutation.isPending}
                    className="text-sm text-gray-400 hover:text-gold-500 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                  >
                    <RefreshCw className={`h-4 w-4 ${resendOTPMutation.isPending ? 'animate-spin' : ''}`} />
                    {resendCooldown > 0 ? (
                      t('auth:buttons.resendCodeIn', { seconds: resendCooldown })
                    ) : (
                      t('auth:buttons.resendCode')
                    )}
                  </button>
                </div>

                {/* Back Button */}
                <div className="text-center pt-4 border-t border-green-900/20">
                  <button
                    type="button"
                    onClick={handleBackToCredentials}
                    disabled={isPending}
                    className="text-sm text-gray-400 hover:text-gold-500 transition-colors disabled:opacity-50"
                  >
                    ← {verifyEmailFromLogin ? t('auth:verification.backToLogin') : t('auth:verification.backToRegistration')}
                  </button>
                </div>
              </form>
            </>
          )}

          {/* Info Message */}
          {registrationStep === 'credentials' && (
            <div className="mt-4 pt-4 border-t border-green-900/20">
              <p className="text-xs text-center text-gray-500">
                {t('auth:hints.walletConnection')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  return createPortal(
    <>
      {modalContent}
      <PasswordResetModal
        isOpen={showPasswordResetModal}
        onClose={() => setShowPasswordResetModal(false)}
      />
    </>,
    document.body
  )
}
